import { useState, useEffect } from 'react';
import { format, addDays, startOfWeek, parseISO, addWeeks, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isSameMonth, addMonths } from 'date-fns';
import { it } from 'date-fns/locale';
import { appointmentsApi, departmentsApi, doctorsApi, patientsApi, formatApiError } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/layout/Layout';
import PageHeader from '../components/layout/PageHeader';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Plus, 
  ChevronLeft, 
  ChevronRight, 
  Loader2, 
  CalendarDays,
  Clock,
  User,
  Stethoscope,
  ArrowRightLeft
} from 'lucide-react';
import { toast } from 'sonner';

const statusColors = {
  confirmed: 'bg-emerald-500',
  pending: 'bg-amber-500',
  cancelled: 'bg-red-500',
  completed: 'bg-sky-500',
  no_show: 'bg-slate-500'
};

const statusLabels = {
  confirmed: 'Confermato',
  pending: 'In attesa',
  cancelled: 'Annullato',
  completed: 'Completato',
  no_show: 'Non presentato'
};

export default function Appointments() {
  const { isAdmin } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [patients, setPatients] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [saving, setSaving] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [rescheduleData, setRescheduleData] = useState({ date: '', time: '' });
  
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [viewMode, setViewMode] = useState('day'); // 'day', 'week', 'month'
  const [allAppointments, setAllAppointments] = useState([]); // for week/month views

  // Load clinic's preferred default view
  useEffect(() => {
    const loadDefaultView = async () => {
      try {
        const res = await appointmentsApi.list({ date: format(new Date(), 'yyyy-MM-dd') });
        // Check if clinic has a preferred view stored in settings
        const settingsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/ai-prompt`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
        });
        if (settingsRes.ok) {
          const settings = await settingsRes.json();
          if (settings.calendar_default_view) {
            setViewMode(settings.calendar_default_view);
          }
        }
      } catch {}
    };
    loadDefaultView();
  }, []);
  
  const [formData, setFormData] = useState({
    patient_id: '',
    department_id: '',
    doctor_id: '',
    service: '',
    date: '',
    time: '',
    duration_minutes: 30,
    notes: '',
    service_type: 'in_clinic',
    home_address: ''
  });

  const [filters, setFilters] = useState({
    department_id: 'all',
    doctor_id: 'all'
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadAppointments();
    if (viewMode !== 'day') loadRangeAppointments();
  }, [selectedDate, filters, viewMode]);

  useEffect(() => {
    if (formData.doctor_id && formData.date) {
      loadAvailableSlots();
    }
  }, [formData.doctor_id, formData.date]);

  const loadInitialData = async () => {
    try {
      const [deptsRes, docsRes, patientsRes] = await Promise.all([
        departmentsApi.list(),
        doctorsApi.list(),
        patientsApi.list()
      ]);
      setDepartments(deptsRes.data.filter(d => d.is_active));
      setDoctors(docsRes.data.filter(d => d.is_active));
      setPatients(patientsRes.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const loadAppointments = async () => {
    try {
      setLoading(true);
      const params = {
        date: format(selectedDate, 'yyyy-MM-dd')
      };
      if (filters.department_id && filters.department_id !== 'all') {
        params.department_id = filters.department_id;
      }
      if (filters.doctor_id && filters.doctor_id !== 'all') {
        params.doctor_id = filters.doctor_id;
      }
      
      const response = await appointmentsApi.list(params);
      setAppointments(response.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const loadRangeAppointments = async () => {
    try {
      let start, end;
      if (viewMode === 'week') {
        start = startOfWeek(selectedDate, { weekStartsOn: 1 });
        end = addDays(start, 6);
      } else {
        start = startOfMonth(selectedDate);
        end = endOfMonth(selectedDate);
      }
      // Single API call for the entire range
      const params = {
        date_from: format(start, 'yyyy-MM-dd'),
        date_to: format(end, 'yyyy-MM-dd')
      };
      if (filters.department_id && filters.department_id !== 'all') params.department_id = filters.department_id;
      if (filters.doctor_id && filters.doctor_id !== 'all') params.doctor_id = filters.doctor_id;
      
      const res = await appointmentsApi.list(params);
      const appts = (res.data || []).map(a => ({ ...a, _date: a.date }));
      setAllAppointments(appts);
    } catch {}
  };

  const loadAvailableSlots = async () => {
    try {
      const response = await appointmentsApi.getAvailableSlots(formData.doctor_id, formData.date);
      setAvailableSlots(response.data);
    } catch (err) {
      setAvailableSlots([]);
    }
  };

  const openNewDialog = () => {
    setFormData({
      patient_id: '',
      department_id: '',
      doctor_id: '',
      service: '',
      date: format(selectedDate, 'yyyy-MM-dd'),
      time: '',
      duration_minutes: 30,
      notes: '',
      service_type: 'in_clinic',
      home_address: ''
    });
    setAvailableSlots([]);
    setDialogOpen(true);
  };

  const openViewDialog = (appt) => {
    setSelectedAppointment(appt);
    setViewDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await appointmentsApi.create(formData);
      toast.success('Appuntamento creato');
      setDialogOpen(false);
      loadAppointments();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (apptId, newStatus) => {
    try {
      await appointmentsApi.update(apptId, { status: newStatus });
      toast.success('Stato aggiornato');
      loadAppointments();
      if (selectedAppointment?.id === apptId) {
        setSelectedAppointment({ ...selectedAppointment, status: newStatus });
      }
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleDelete = async (apptId) => {
    if (!window.confirm('Eliminare questo appuntamento?')) return;
    
    try {
      await appointmentsApi.delete(apptId);
      toast.success('Appuntamento eliminato');
      setViewDialogOpen(false);
      loadAppointments();
      if (viewMode !== 'day') loadRangeAppointments();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleReschedule = async () => {
    if (!rescheduleData.date || !rescheduleData.time) {
      toast.error('Seleziona data e ora');
      return;
    }
    setSaving(true);
    try {
      await appointmentsApi.update(selectedAppointment.id, {
        date: rescheduleData.date,
        time: rescheduleData.time,
        notes: `${selectedAppointment.notes || ''}\nSpostato da ${selectedAppointment.date} ${selectedAppointment.time}`.trim()
      });
      
      // Send reschedule email
      try {
        const token = localStorage.getItem('access_token');
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/appointments/${selectedAppointment.id}/notify-reschedule`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            old_date: selectedAppointment.date,
            old_time: selectedAppointment.time,
            new_date: rescheduleData.date,
            new_time: rescheduleData.time
          })
        });
      } catch {}

      toast.success('Appuntamento spostato!');
      setRescheduleOpen(false);
      setViewDialogOpen(false);
      loadAppointments();
      if (viewMode !== 'day') loadRangeAppointments();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const filteredDoctors = formData.department_id 
    ? doctors.filter(d => d.department_id === formData.department_id)
    : doctors;

  const selectedDept = departments.find(d => d.id === formData.department_id);

  // Generate time slots for the day view
  const timeSlots = [];
  for (let h = 8; h < 20; h++) {
    timeSlots.push(`${h.toString().padStart(2, '0')}:00`);
    timeSlots.push(`${h.toString().padStart(2, '0')}:30`);
  }

  return (
    <Layout>
      <PageHeader 
        title="Appuntamenti" 
        subtitle={`Agenda del ${format(selectedDate, 'EEEE d MMMM yyyy', { locale: it })}`}
        actions={
          <Button 
            onClick={openNewDialog} 
            className="bg-sky-500 hover:bg-sky-600"
            data-testid="add-appointment-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuovo Appuntamento
          </Button>
        }
      />

      <div className="content-area" data-testid="appointments-page">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar with Calendar and Filters */}
          <div className="space-y-6">
            {/* Calendar */}
            <div className="calendar-widget">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => date && setSelectedDate(date)}
                locale={it}
                className="rounded-md"
                data-testid="appointments-calendar"
              />
            </div>

            {/* Filters */}
            <div className="bg-white border border-slate-200/60 rounded-lg p-4">
              <h3 className="font-semibold text-slate-800 mb-4" style={{ fontFamily: 'Manrope' }}>
                Filtri
              </h3>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Reparto</Label>
                  <Select
                    value={filters.department_id}
                    onValueChange={(value) => setFilters({ ...filters, department_id: value, doctor_id: 'all' })}
                  >
                    <SelectTrigger className="mt-1" data-testid="filter-dept">
                      <SelectValue placeholder="Tutti i reparti" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutti i reparti</SelectItem>
                      {departments.map((dept) => (
                        <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Medico</Label>
                  <Select
                    value={filters.doctor_id}
                    onValueChange={(value) => setFilters({ ...filters, doctor_id: value })}
                  >
                    <SelectTrigger className="mt-1" data-testid="filter-doctor">
                      <SelectValue placeholder="Tutti i medici" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutti i medici</SelectItem>
                      {doctors
                        .filter(d => filters.department_id === 'all' || d.department_id === filters.department_id)
                        .map((doc) => (
                          <SelectItem key={doc.id} value={doc.id}>{doc.name}</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>

          {/* Main Agenda View */}
          <div className="lg:col-span-3">
            <div className="bg-white border border-slate-200/60 rounded-lg overflow-hidden">
              {/* View Mode Tabs + Navigation */}
              <div className="flex items-center justify-between px-6 py-3 border-b border-slate-200/60 bg-slate-50">
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      if (viewMode === 'day') setSelectedDate(addDays(selectedDate, -1));
                      else if (viewMode === 'week') setSelectedDate(addDays(selectedDate, -7));
                      else setSelectedDate(addMonths(selectedDate, -1));
                    }}
                    data-testid="prev-btn"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <h3 className="font-semibold text-slate-800 min-w-[200px] text-center" style={{ fontFamily: 'Manrope' }}>
                    {viewMode === 'day' && format(selectedDate, 'EEEE d MMMM', { locale: it })}
                    {viewMode === 'week' && `${format(startOfWeek(selectedDate, { weekStartsOn: 1 }), 'd', { locale: it })} - ${format(addDays(startOfWeek(selectedDate, { weekStartsOn: 1 }), 6), 'd MMMM yyyy', { locale: it })}`}
                    {viewMode === 'month' && format(selectedDate, 'MMMM yyyy', { locale: it })}
                  </h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      if (viewMode === 'day') setSelectedDate(addDays(selectedDate, 1));
                      else if (viewMode === 'week') setSelectedDate(addDays(selectedDate, 7));
                      else setSelectedDate(addMonths(selectedDate, 1));
                    }}
                    data-testid="next-btn"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex bg-slate-200 rounded-xl p-1 gap-1" data-testid="view-mode-tabs">
                  {[
                    { mode: 'day', label: 'Giorno', icon: '📅' },
                    { mode: 'week', label: 'Settimana', icon: '📋' },
                    { mode: 'month', label: 'Mese', icon: '🗓' }
                  ].map(v => (
                    <button
                      key={v.mode}
                      onClick={() => setViewMode(v.mode)}
                      className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
                        viewMode === v.mode
                          ? 'bg-sky-500 text-white shadow-md shadow-sky-200'
                          : 'text-slate-600 hover:text-slate-800 hover:bg-slate-100'
                      }`}
                      data-testid={`view-${v.mode}`}
                    >
                      {v.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* DAY VIEW */}
              {viewMode === 'day' && (
                <div className="divide-y divide-slate-100">
                  {loading ? (
                    <div className="py-12 text-center">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                    </div>
                  ) : appointments.length === 0 ? (
                    <div className="py-12 text-center text-slate-500">
                      <CalendarDays className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                      <p>Nessun appuntamento per questa data</p>
                    </div>
                  ) : (
                    appointments.map((appt) => (
                      <div 
                        key={appt.id}
                        className="flex items-center gap-4 px-6 py-4 hover:bg-slate-50 cursor-pointer transition-colors"
                        onClick={() => openViewDialog(appt)}
                        data-testid={`appointment-${appt.id}`}
                      >
                        <div className="text-center min-w-[60px]">
                          <p className="text-lg font-semibold text-slate-800">{appt.time}</p>
                          <p className="text-xs text-slate-500">{appt.duration_minutes} min</p>
                        </div>
                        <div className={`w-1 h-12 rounded-full ${statusColors[appt.status] || 'bg-slate-300'}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-slate-800">{appt.patient_name}</p>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                              {appt.service}
                            </span>
                            {appt.service_type === 'home_visit' && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">
                                Domicilio
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                            <span className="flex items-center gap-1">
                              <Stethoscope className="w-3 h-3" />
                              {appt.doctor_name}
                            </span>
                            <span>{appt.department_name}</span>
                          </div>
                        </div>
                        <span className={`badge ${
                          appt.status === 'confirmed' ? 'badge-confirmed' :
                          appt.status === 'pending' ? 'badge-pending' :
                          appt.status === 'cancelled' ? 'badge-cancelled' :
                          appt.status === 'completed' ? 'badge-completed' : ''
                        }`}>
                          {statusLabels[appt.status] || appt.status}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* WEEK VIEW */}
              {viewMode === 'week' && (() => {
                const ws = startOfWeek(selectedDate, { weekStartsOn: 1 });
                const weekDays = Array.from({ length: 7 }, (_, i) => addDays(ws, i));
                return (
                  <div className="grid grid-cols-7 divide-x divide-slate-100 min-h-[400px]" data-testid="week-view">
                    {weekDays.map((day, idx) => {
                      const dayStr = format(day, 'yyyy-MM-dd');
                      const dayAppts = allAppointments.filter(a => (a._date || a.date) === dayStr);
                      const isToday = isSameDay(day, new Date());
                      const isWeekend = idx >= 5;
                      return (
                        <div key={dayStr} className={`flex flex-col ${isWeekend ? 'bg-slate-50/50' : ''}`}>
                          <div className={`text-center py-2 border-b border-slate-100 ${isToday ? 'bg-sky-50' : ''}`}>
                            <p className="text-[10px] text-slate-400 uppercase">{format(day, 'EEE', { locale: it })}</p>
                            <p className={`text-sm font-bold ${isToday ? 'text-sky-600' : 'text-slate-700'}`}>{format(day, 'd')}</p>
                          </div>
                          <div className="flex-1 p-1 space-y-1 overflow-y-auto max-h-[350px]">
                            {dayAppts.length === 0 && (
                              <p className="text-[10px] text-slate-300 text-center mt-4">—</p>
                            )}
                            {dayAppts.map((a, i) => (
                              <div
                                key={i}
                                onClick={() => { setSelectedDate(day); openViewDialog(a); }}
                                className={`p-1.5 rounded text-[10px] cursor-pointer hover:opacity-80 transition-opacity ${
                                  a.status === 'confirmed' ? 'bg-emerald-100 text-emerald-800' :
                                  a.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                                  'bg-sky-100 text-sky-800'
                                }`}
                              >
                                <p className="font-bold">{a.time}</p>
                                <p className="truncate">{a.patient_name}</p>
                                <p className="truncate opacity-70">{a.service}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              {/* MONTH VIEW */}
              {viewMode === 'month' && (() => {
                const monthStart = startOfMonth(selectedDate);
                const monthEnd = endOfMonth(selectedDate);
                const calStart = startOfWeek(monthStart, { weekStartsOn: 1 });
                const calEnd = addDays(startOfWeek(addDays(monthEnd, 7), { weekStartsOn: 1 }), -1);
                const calDays = eachDayOfInterval({ start: calStart, end: calEnd });
                return (
                  <div data-testid="month-view">
                    {/* Day headers */}
                    <div className="grid grid-cols-7 border-b border-slate-100">
                      {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map(d => (
                        <div key={d} className="text-center py-1.5 text-[10px] font-semibold text-slate-400 uppercase">{d}</div>
                      ))}
                    </div>
                    <div className="grid grid-cols-7 divide-x divide-y divide-slate-100">
                      {calDays.map(day => {
                        const dayStr = format(day, 'yyyy-MM-dd');
                        const dayAppts = allAppointments.filter(a => (a._date || a.date) === dayStr);
                        const isToday = isSameDay(day, new Date());
                        const isCurrentMonth = isSameMonth(day, selectedDate);
                        return (
                          <div
                            key={dayStr}
                            className={`min-h-[80px] p-1 cursor-pointer hover:bg-sky-50/50 transition-colors ${!isCurrentMonth ? 'opacity-30' : ''}`}
                            onClick={() => { setSelectedDate(day); setViewMode('day'); }}
                          >
                            <p className={`text-xs font-medium mb-0.5 ${isToday ? 'text-sky-600 font-bold' : 'text-slate-600'}`}>
                              {format(day, 'd')}
                            </p>
                            {dayAppts.slice(0, 3).map((a, i) => (
                              <div key={i} className={`text-[9px] px-1 py-0.5 rounded mb-0.5 truncate ${
                                a.status === 'confirmed' ? 'bg-emerald-100 text-emerald-700' :
                                a.status === 'cancelled' ? 'bg-red-100 text-red-600' :
                                'bg-sky-100 text-sky-700'
                              }`}>
                                {a.time} {a.patient_name}
                              </div>
                            ))}
                            {dayAppts.length > 3 && (
                              <p className="text-[9px] text-slate-400 pl-1">+{dayAppts.length - 3} altri</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* New Appointment Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="new-appointment-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>
              Nuovo Appuntamento
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label>Paziente *</Label>
              <Select
                value={formData.patient_id}
                onValueChange={(value) => setFormData({ ...formData, patient_id: value })}
              >
                <SelectTrigger className="mt-1.5" data-testid="select-patient">
                  <SelectValue placeholder="Seleziona paziente" />
                </SelectTrigger>
                <SelectContent>
                  {patients.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} - {p.phone}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Reparto *</Label>
              <Select
                value={formData.department_id}
                onValueChange={(value) => setFormData({ 
                  ...formData, 
                  department_id: value, 
                  doctor_id: '',
                  service: '' 
                })}
              >
                <SelectTrigger className="mt-1.5" data-testid="select-dept">
                  <SelectValue placeholder="Seleziona reparto" />
                </SelectTrigger>
                <SelectContent>
                  {departments.map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {formData.department_id && (
              <>
                <div>
                  <Label>Servizio *</Label>
                  <Select
                    value={formData.service}
                    onValueChange={(value) => setFormData({ ...formData, service: value })}
                  >
                    <SelectTrigger className="mt-1.5" data-testid="select-service">
                      <SelectValue placeholder="Seleziona servizio" />
                    </SelectTrigger>
                    <SelectContent>
                      {selectedDept?.services?.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Medico *</Label>
                  <Select
                    value={formData.doctor_id}
                    onValueChange={(value) => setFormData({ ...formData, doctor_id: value })}
                  >
                    <SelectTrigger className="mt-1.5" data-testid="select-doctor">
                      <SelectValue placeholder="Seleziona medico" />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredDoctors.map((d) => (
                        <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Data *</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value, time: '' })}
                  className="mt-1.5"
                  min={format(new Date(), 'yyyy-MM-dd')}
                  data-testid="input-date"
                />
              </div>
              <div>
                <Label>Orario *</Label>
                <Select
                  value={formData.time}
                  onValueChange={(value) => setFormData({ ...formData, time: value })}
                  disabled={!formData.doctor_id || !formData.date}
                >
                  <SelectTrigger className="mt-1.5" data-testid="select-time">
                    <SelectValue placeholder={availableSlots.length > 0 ? "Seleziona orario" : "Nessuno slot"} />
                  </SelectTrigger>
                  <SelectContent>
                    {availableSlots.map((slot) => (
                      <SelectItem key={slot.time} value={slot.time}>{slot.time}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Service Type: In clinic / Home visit */}
            <div>
              <Label>Tipo di servizio</Label>
              <div className="flex gap-2 mt-1.5">
                <button type="button"
                  onClick={() => setFormData({ ...formData, service_type: 'in_clinic', home_address: '' })}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium border transition-colors ${formData.service_type === 'in_clinic' ? 'bg-sky-50 border-sky-300 text-sky-700' : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'}`}
                  data-testid="service-type-clinic">
                  In ambulatorio
                </button>
                <button type="button"
                  onClick={() => setFormData({ ...formData, service_type: 'home_visit' })}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium border transition-colors ${formData.service_type === 'home_visit' ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'}`}
                  data-testid="service-type-home">
                  A domicilio
                </button>
              </div>
            </div>

            {formData.service_type === 'home_visit' && (
              <div>
                <Label>Indirizzo domicilio *</Label>
                <Input
                  value={formData.home_address}
                  onChange={(e) => setFormData({ ...formData, home_address: e.target.value })}
                  placeholder="Via Roma 1, 10100 Torino"
                  className="mt-1.5"
                  data-testid="input-home-address"
                />
              </div>
            )}

            <div>
              <Label>Note</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Note aggiuntive..."
                className="mt-1.5"
                rows={2}
                data-testid="input-notes"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annulla
              </Button>
              <Button 
                type="submit" 
                disabled={saving || !formData.patient_id || !formData.doctor_id || !formData.time}
                className="bg-sky-500 hover:bg-sky-600"
                data-testid="submit-appointment-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creazione...
                  </>
                ) : (
                  'Crea Appuntamento'
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Appointment Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="view-appointment-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>
              Dettagli Appuntamento
            </DialogTitle>
          </DialogHeader>
          {selectedAppointment && (
            <div className="mt-4 space-y-4">
              <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  statusColors[selectedAppointment.status]?.replace('bg-', 'bg-opacity-20 ') || 'bg-slate-200'
                }`}>
                  <CalendarDays className={`w-6 h-6 ${
                    selectedAppointment.status === 'confirmed' ? 'text-emerald-600' :
                    selectedAppointment.status === 'pending' ? 'text-amber-600' :
                    selectedAppointment.status === 'cancelled' ? 'text-red-600' :
                    'text-sky-600'
                  }`} />
                </div>
                <div>
                  <p className="font-semibold text-slate-800">{selectedAppointment.date}</p>
                  <p className="text-slate-600">{selectedAppointment.time} - {selectedAppointment.duration_minutes} min</p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <User className="w-4 h-4 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">Paziente</p>
                    <p className="font-medium text-slate-800">{selectedAppointment.patient_name}</p>
                    <p className="text-sm text-slate-600">{selectedAppointment.patient_phone}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Stethoscope className="w-4 h-4 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">Medico</p>
                    <p className="font-medium text-slate-800">{selectedAppointment.doctor_name}</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Servizio</p>
                  <p className="font-medium text-slate-800">{selectedAppointment.service}</p>
                  <p className="text-sm text-slate-600">{selectedAppointment.department_name}</p>
                </div>
                {selectedAppointment.notes && (
                  <div>
                    <p className="text-sm text-slate-500">Note</p>
                    <p className="text-slate-800">{selectedAppointment.notes}</p>
                  </div>
                )}
              </div>

              <div>
                <p className="text-sm text-slate-500 mb-2">Stato</p>
                <Select
                  value={selectedAppointment.status}
                  onValueChange={(value) => handleStatusChange(selectedAppointment.id, value)}
                >
                  <SelectTrigger data-testid="change-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">In attesa</SelectItem>
                    <SelectItem value="confirmed">Confermato</SelectItem>
                    <SelectItem value="completed">Completato</SelectItem>
                    <SelectItem value="cancelled">Annullato</SelectItem>
                    <SelectItem value="no_show">Non presentato</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {isAdmin && (
                <div className="pt-4 border-t border-slate-200 space-y-2">
                  {selectedAppointment.status !== 'cancelled' && (
                    <Button
                      variant="outline"
                      className="w-full text-sky-600 hover:text-sky-700 hover:bg-sky-50"
                      onClick={() => {
                        setRescheduleData({ date: '', time: '' });
                        setRescheduleOpen(true);
                      }}
                      data-testid="reschedule-appointment-btn"
                    >
                      <ArrowRightLeft className="w-4 h-4 mr-2" />
                      Sposta Appuntamento
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => handleDelete(selectedAppointment.id)}
                    data-testid="delete-appointment-btn"
                  >
                    Elimina Appuntamento
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Reschedule Dialog */}
      <Dialog open={rescheduleOpen} onOpenChange={setRescheduleOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-sky-500" />
              Sposta Appuntamento
            </DialogTitle>
          </DialogHeader>
          {selectedAppointment && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-3 text-sm">
                <p className="font-medium text-slate-700">{selectedAppointment.patient_name}</p>
                <p className="text-slate-500">{selectedAppointment.service}</p>
                <p className="text-slate-400 text-xs mt-1">
                  Attuale: {selectedAppointment.date} ore {selectedAppointment.time}
                </p>
              </div>
              <div>
                <Label>Nuova Data</Label>
                <Input
                  type="date"
                  value={rescheduleData.date}
                  onChange={(e) => setRescheduleData({...rescheduleData, date: e.target.value})}
                  className="mt-1"
                  data-testid="reschedule-date"
                />
              </div>
              <div>
                <Label>Nuovo Orario</Label>
                <Select value={rescheduleData.time} onValueChange={(v) => setRescheduleData({...rescheduleData, time: v})}>
                  <SelectTrigger className="mt-1" data-testid="reschedule-time">
                    <SelectValue placeholder="Seleziona orario" />
                  </SelectTrigger>
                  <SelectContent>
                    {['09:00','09:30','10:00','10:30','11:00','11:30','14:00','14:30','15:00','15:30','16:00','16:30'].map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setRescheduleOpen(false)}>
                  Annulla
                </Button>
                <Button className="flex-1 bg-sky-500 hover:bg-sky-600" onClick={handleReschedule} disabled={saving} data-testid="confirm-reschedule-btn">
                  {saving ? 'Spostamento...' : 'Conferma Spostamento'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </Layout>
  );
}
